import { affineBruteForce } from "@/lib/tool-kit/ciphers/affine";
import {
  ALPHABET_SIZE,
  foldDiacritics,
  letterAt,
  letterIndex,
  lettersOnly,
  mod,
} from "@/lib/tool-kit/ciphers/alphabet";
import { atbash } from "@/lib/tool-kit/ciphers/atbash";
import { englishChiSquared } from "@/lib/tool-kit/ciphers/english-frequency";
import { englishPairScore } from "@/lib/tool-kit/ciphers-misc/english-ngrams";

/**
 * Guess which family of cipher a piece of text belongs to.
 *
 * This is a set of rules over simple measurements, not a solver and not a classifier
 * trained on data: character set, how the text is grouped, the index of coincidence,
 * how English its letter pairs look, how the best period of a repeating key shows up
 * in column statistics, and repeated-trigram distances (Kasiski). Each guess carries a
 * strength — strong, possible or weak — and the reasons for it; no probabilities are
 * claimed, because the rules cannot back them. Short texts weaken every statistic.
 */

export const ENGLISH_IC = 0.0667;
export const RANDOM_IC = 1 / ALPHABET_SIZE;

export type FamilyId =
  | "english"
  | "shift"
  | "atbash"
  | "affine"
  | "substitution"
  | "transposition"
  | "periodic"
  | "digraph"
  | "flat"
  | "adfgvx"
  | "bacon"
  | "morse"
  | "binary"
  | "a1z26"
  | "polybius"
  | "book"
  | "phone"
  | "hex"
  | "ascii-numbers"
  | "base32"
  | "base64"
  | "ascii85"
  | "braille"
  | "symbols"
  | "other-script";

export type Level = "strong" | "possible" | "weak";

export interface Reason {
  key: string;
  params?: Record<string, number | string>;
}

export interface Finding {
  family: FamilyId;
  level: Level;
  reasons: Reason[];
}

export interface PeriodGuess {
  length: number;
  /** Mean index of coincidence of the columns for that key length. */
  ic: number;
  /** How many standard deviations above the IC of random letters that is. */
  sigma: number;
}

export interface Statistics {
  /** Characters excluding whitespace. */
  characters: number;
  letters: number;
  digits: number;
  /** Distinct non-space characters. */
  distinct: number;
  distinctLetters: number;
  hasSpaces: boolean;
  ic: number | null;
  pairScore: number | null;
  /** Chi-squared against English letter frequencies, divided by the letter count. */
  chiPerLetter: number | null;
  periods: PeriodGuess[];
  /** Key lengths that divide many of the distances between repeated trigrams. */
  kasiski: { factor: number; count: number }[];
}

export interface Identification {
  stats: Statistics;
  findings: Finding[];
  /** Too few letters for the statistics to mean much. */
  short: boolean;
}

/** Below this many letters the statistics are noise. */
export const SHORT_TEXT = 40;
const MAX_PERIOD = 14;
const MIN_COLUMN = 2;
const KASISKI_MIN = 3;
const KASISKI_MAX_FACTOR = 20;

/* Thresholds, found by running the rules over known ciphers of 150–400 letters. */
const READS_AS_ENGLISH = 0.6;
const READS_AS_ENGLISH_SHORT = 0.45;
const SHIFT_MARGIN = 1.4;
const ENGLISH_LIKE_IC = 0.058;
const PERIODIC_IC = 0.052;
const PERIODIC_GAIN = 0.008;
const PERIODIC_SIGMA = 4;
const PERIOD_TIE = 0.004;
const PREVIEW_LENGTH = 48;
const DIGRAPH_STRONG_PAIRS = 100;
const DIGRAPH_MIN_PAIRS = 40;
const SAME_LETTERS_CHI = 0.6;
const FLAT_IC = 0.047;

const WHITESPACE = /\s/u;
const ANY_LETTER = /\p{L}/u;
const DIGIT = /\d/;
const DIGIT_RUN = /\d+/g;
const MORSE_CHARS = /^[.\-/|\s]+$/;
const BINARY_CHARS = /^[01\s]+$/;
const HEX_CHARS = /^(?:0x)?[\da-f\s,:]+$/i;
const HEX_TOKEN = /^(?:0x)?[\da-f]{2}$/i;
const BASE64_CHARS = /^[A-Za-z\d+/_-]+={0,2}$/;
const BASE32_CHARS = /^[A-Z2-7]+=*$/;
const BRAILLE = /[⠀-⣿]/u;
const ADFGVX_LETTERS = /^[ADFGVX\s]+$/i;
const POLYBIUS_TOKEN = /^[1-5]{2}$/;
const POLYBIUS_RUN = /^[1-5]+$/;
const NUMBER_SEPARATORS = /[\s,;:-]/g;
const NUMBER_SPLIT = /\s+|[,;:-]+/;
const MORSE_MARK = /[.-]/;
const ANY_SPACE = /\s/g;
const SPACES = /\s+/;
const HEX_SPLIT = /[\s,:]+/;
const HEX_SEPARATORS = /[\s,:]/g;
const HEX_LETTER = /[a-f]/i;
const BASE32_DIGIT = /[2-7]/;
const UPPER = /[A-Z]/;
const LOWER = /[a-z]/;
const T9_TOKEN = /^[2-9]+$/;
const BITS_TOKEN = /^[01]+$/;
const PHONE_TOKEN = /^([2-9])\1*$/;
const ASCII85_WRAP = /^<~[\s\S]*~>$/;

/* ------------------------------------------------------------- statistics */

export function indexOfCoincidence(letters: string): number {
  const counts = Array.from({ length: ALPHABET_SIZE }, () => 0);
  for (const char of letters) {
    counts[letterIndex(char)] += 1;
  }
  const total = letters.length;
  if (total < 2) {
    return 0;
  }
  let pairs = 0;
  for (const count of counts) {
    pairs += count * (count - 1);
  }
  return pairs / (total * (total - 1));
}

/**
 * Mean index of coincidence when the letters are dealt into `period` columns, and how many
 * letter pairs those columns hold (which sets how much the mean can wobble by chance).
 */
export function columnIc(
  letters: string,
  period: number
): { ic: number; pairs: number } {
  const columns = Array.from({ length: period }, () => "");
  for (const [index, char] of [...letters].entries()) {
    columns[index % period] += char;
  }
  const usable = columns.filter((column) => column.length >= MIN_COLUMN);
  if (usable.length === 0) {
    return { ic: 0, pairs: 0 };
  }
  const total = usable.reduce(
    (sum, column) => sum + indexOfCoincidence(column),
    0
  );
  const pairs = usable.reduce(
    (sum, column) => sum + (column.length * (column.length - 1)) / 2,
    0
  );
  return { ic: total / usable.length, pairs };
}

/**
 * Random letters have IC ≈ 1/26, and the mean over columns wobbles by about
 * √(p(1−p) ÷ pairs) around it. Measuring a period's IC in those units keeps a long guessed
 * key on a short text, whose columns hold a handful of letters, from looking significant.
 */
function periodSigma(ic: number, pairs: number): number {
  if (pairs === 0) {
    return 0;
  }
  return (ic - RANDOM_IC) / Math.sqrt((RANDOM_IC * (1 - RANDOM_IC)) / pairs);
}

function periodGuesses(letters: string): PeriodGuess[] {
  const limit = Math.min(MAX_PERIOD, Math.floor(letters.length / 6));
  const guesses: PeriodGuess[] = [];
  for (let length = 2; length <= limit; length += 1) {
    const { ic, pairs } = columnIc(letters, length);
    guesses.push({ length, ic, sigma: periodSigma(ic, pairs) });
  }
  return guesses.sort((a, b) => b.ic - a.ic || a.length - b.length);
}

/** For repeated trigrams, how often each small factor divides the distance between repeats. */
export function kasiskiFactors(
  letters: string
): { factor: number; count: number }[] {
  const seen = new Map<string, number>();
  const distances: number[] = [];
  for (let index = 0; index + 3 <= letters.length; index += 1) {
    const gram = letters.slice(index, index + 3);
    const before = seen.get(gram);
    if (before !== undefined) {
      distances.push(index - before);
    }
    seen.set(gram, index);
  }
  if (distances.length < KASISKI_MIN) {
    return [];
  }
  const tally: { factor: number; count: number }[] = [];
  for (let factor = 2; factor <= KASISKI_MAX_FACTOR; factor += 1) {
    const count = distances.filter(
      (distance) => distance % factor === 0
    ).length;
    tally.push({ factor, count });
  }
  return tally
    .filter((entry) => entry.count >= KASISKI_MIN)
    .sort((a, b) => b.count - a.count || a.factor - b.factor)
    .slice(0, 3);
}

function measure(text: string): Statistics {
  const folded = foldDiacritics(text);
  const letters = lettersOnly(text);
  const visible = [...text].filter((char) => !WHITESPACE.test(char));
  const enough = letters.length >= MIN_COLUMN * 2;
  return {
    characters: visible.length,
    letters: letters.length,
    digits: [...folded].filter((char) => DIGIT.test(char)).length,
    distinct: new Set(visible).size,
    distinctLetters: new Set(letters).size,
    hasSpaces: [...text.trim()].some((char) => WHITESPACE.test(char)),
    ic: enough ? indexOfCoincidence(letters) : null,
    pairScore: enough ? englishPairScore(letters) : null,
    chiPerLetter: enough ? englishChiSquared(letters) / letters.length : null,
    periods: letters.length >= 12 ? periodGuesses(letters) : [],
    kasiski: kasiskiFactors(letters),
  };
}

/* ----------------------------------------------------- character-set rules */

function found(family: FamilyId, level: Level, ...reasons: Reason[]): Finding {
  return { family, level, reasons };
}

function numbersIn(text: string): number[] {
  return (text.match(DIGIT_RUN) ?? []).map(Number);
}

function onlyDigitsAndSeparators(text: string): boolean {
  return [...text].every((char) => DIGIT.test(char) || !ANY_LETTER.test(char));
}

function numberTokens(text: string): string[] {
  return text
    .trim()
    .split(NUMBER_SPLIT)
    .filter((token) => token.length > 0);
}

/** Digit groups made only of 2–9 (and lone 0s) are phone-keypad digits. */
function phoneFinding(tokens: string[]): Finding | null {
  const lone = tokens.filter((token) => token === "0").length;
  const words = tokens.filter((token) => token !== "0");
  if (tokens.length > 1 && words.every((token) => PHONE_TOKEN.test(token))) {
    return found("phone", "strong", { key: "phone-runs" });
  }
  const t9 = words.every((token) => T9_TOKEN.test(token));
  if (t9 && (lone > 0 || tokens.length === 1)) {
    return found("phone", "possible", { key: "phone-digits" });
  }
  return null;
}

function squareFinding(text: string, tokens: string[]): Finding | null {
  if (tokens.length > 1 && tokens.every((t) => POLYBIUS_TOKEN.test(t))) {
    return found("polybius", "strong", { key: "polybius-pairs" });
  }
  const digits = text.replace(NUMBER_SEPARATORS, "");
  if (POLYBIUS_RUN.test(digits) && digits.length % 2 === 0) {
    return found("polybius", "possible", { key: "polybius-run" });
  }
  return null;
}

function numericFindings(text: string, stats: Statistics): Finding[] {
  const tokens = numberTokens(text);
  const isBits = tokens.every((token) => BITS_TOKEN.test(token));
  if (
    stats.digits === 0 ||
    stats.letters > 0 ||
    !onlyDigitsAndSeparators(text) ||
    isBits
  ) {
    return [];
  }
  const numbers = numbersIn(text);
  const spaced = tokens.length > 1;
  const results: (Finding | null)[] = [phoneFinding(tokens)];
  if (spaced && numbers.every((n) => n >= 1 && n <= ALPHABET_SIZE)) {
    results.push(found("a1z26", "strong", { key: "a1z26-range" }));
  }
  results.push(squareFinding(text, tokens));
  if (spaced && numbers.every((n) => n >= 32 && n <= 126)) {
    results.push(found("ascii-numbers", "strong", { key: "ascii-range" }));
  }
  const claimed = results.some((result) => result !== null);
  if (!claimed && spaced && numbers.some((n) => n > ALPHABET_SIZE)) {
    results.push(
      found("book", "possible", {
        key: "book-numbers",
        params: { max: Math.max(...numbers) },
      })
    );
  }
  return results.filter((result): result is Finding => result !== null);
}

function bitFindings(trimmed: string): Finding[] {
  const groups = trimmed.split(SPACES);
  if (groups.length > 1 && groups.every((group) => group.length === 8)) {
    return [found("binary", "strong", { key: "binary-bytes" })];
  }
  const bits = trimmed.replace(ANY_SPACE, "").length;
  const results: Finding[] = [];
  if (bits % 8 === 0) {
    results.push(found("binary", "possible", { key: "binary-digits" }));
  }
  if (bits % 5 === 0) {
    results.push(found("bacon", "possible", { key: "bacon-bits" }));
  }
  return results.length > 0
    ? results
    : [found("binary", "weak", { key: "binary-digits" })];
}

function symbolFindings(text: string, stats: Statistics): Finding[] {
  const trimmed = text.trim();
  if (BRAILLE.test(trimmed)) {
    return [found("braille", "strong", { key: "braille-cells" })];
  }
  if (MORSE_CHARS.test(trimmed) && MORSE_MARK.test(trimmed)) {
    return [found("morse", "strong", { key: "morse-chars" })];
  }
  if (BINARY_CHARS.test(trimmed) && stats.digits > 0) {
    return bitFindings(trimmed);
  }
  if (ASCII85_WRAP.test(trimmed)) {
    return [found("ascii85", "strong", { key: "ascii85-markers" })];
  }
  return [];
}

function hexFindings(text: string): Finding[] {
  const trimmed = text.trim();
  if (!(HEX_CHARS.test(trimmed) && HEX_LETTER.test(trimmed))) {
    return [];
  }
  const tokens = trimmed.split(HEX_SPLIT);
  if (tokens.length > 1 && tokens.every((token) => HEX_TOKEN.test(token))) {
    return [found("hex", "strong", { key: "hex-bytes" })];
  }
  const joined = trimmed.replace(HEX_SEPARATORS, "");
  if (joined.length % 2 === 0 && joined.length >= 8) {
    return [found("hex", "possible", { key: "hex-run" })];
  }
  return [];
}

function encodingFindings(text: string): Finding[] {
  const trimmed = text.trim();
  if (trimmed.length < 8 || WHITESPACE.test(trimmed)) {
    return [];
  }
  const padded = trimmed.endsWith("=");
  if (BASE32_CHARS.test(trimmed) && (padded || BASE32_DIGIT.test(trimmed))) {
    const exact = padded && trimmed.length % 8 === 0;
    return [
      found("base32", exact ? "strong" : "possible", {
        key: exact ? "base32-padded" : "base32-alphabet",
      }),
    ];
  }
  const mixed =
    UPPER.test(trimmed) && LOWER.test(trimmed) && DIGIT.test(trimmed);
  if (BASE64_CHARS.test(trimmed) && (padded || mixed)) {
    const exact = padded && trimmed.length % 4 === 0;
    return [
      found("base64", exact ? "strong" : "possible", {
        key: exact ? "base64-padded" : "base64-alphabet",
      }),
    ];
  }
  return [];
}

function twoLetterFindings(text: string, stats: Statistics): Finding[] {
  if (stats.letters < 10 || stats.digits > 0) {
    return [];
  }
  if (ADFGVX_LETTERS.test(text.trim()) && stats.distinctLetters >= 4) {
    return [
      found("adfgvx", "strong", {
        key: "adfgvx-letters",
        params: { distinct: stats.distinctLetters },
      }),
    ];
  }
  if (stats.distinctLetters === 2 && stats.letters % 5 === 0) {
    return [found("bacon", "strong", { key: "bacon-letters" })];
  }
  return [];
}

/** Findings that depend only on which characters appear and how they are grouped. */
function structuralFindings(text: string, stats: Statistics): Finding[] {
  return [
    ...symbolFindings(text, stats),
    ...numericFindings(text, stats),
    ...hexFindings(text),
    ...encodingFindings(text),
    ...twoLetterFindings(text, stats),
  ];
}

/* ---------------------------------------------------------- letter ciphers */

interface Shifted {
  shift: number;
  text: string;
  score: number;
  /** The best score among the other 25 shifts. */
  runnerUp: number;
}

function bestShift(letters: string): Shifted {
  const scored = Array.from({ length: ALPHABET_SIZE }, (_, shift) => {
    const text = [...letters]
      .map((char) => letterAt(mod(letterIndex(char) - shift, ALPHABET_SIZE)))
      .join("");
    return { shift, text, score: englishPairScore(text) };
  }).sort((a, b) => b.score - a.score || a.shift - b.shift);
  return { ...scored[0], runnerUp: scored[1]?.score ?? 0 };
}

/** What counts as readable depends on length: a short text is held to a looser bar. */
function readableBar(stats: Statistics): number {
  return stats.letters < SHORT_TEXT ? READS_AS_ENGLISH_SHORT : READS_AS_ENGLISH;
}

function simpleSubstitutionFinding(
  letters: string,
  stats: Statistics
): Finding | null {
  const bar = readableBar(stats);
  const readable = stats.pairScore ?? 0;
  if (readable >= bar) {
    return found("english", "strong", { key: "english-pairs" });
  }
  const shifted = bestShift(letters);
  if (
    shifted.score >= bar &&
    shifted.score >= SHIFT_MARGIN * shifted.runnerUp
  ) {
    return found("shift", "strong", {
      key: "shift-reads",
      params: {
        shift: shifted.shift,
        preview: shifted.text.slice(0, PREVIEW_LENGTH),
      },
    });
  }
  const mirrored = atbash(letters);
  if (englishPairScore(mirrored) >= bar) {
    return found("atbash", "strong", {
      key: "atbash-reads",
      params: { preview: mirrored.slice(0, PREVIEW_LENGTH) },
    });
  }
  const affine =
    stats.letters < SHORT_TEXT ? undefined : affineBruteForce(letters)[0];
  if (affine && englishPairScore(affine.text) >= bar) {
    return found("affine", "strong", {
      key: "affine-reads",
      params: {
        a: affine.a,
        b: affine.b,
        preview: affine.text.slice(0, PREVIEW_LENGTH),
      },
    });
  }
  return null;
}

function englishLikeFindings(stats: Statistics): Finding[] {
  const chi = stats.chiPerLetter ?? Number.POSITIVE_INFINITY;
  const ic = stats.ic ?? 0;
  if (chi <= SAME_LETTERS_CHI) {
    return [
      {
        family: "transposition",
        level: "strong",
        reasons: [
          { key: "ic-english", params: { ic } },
          { key: "letters-in-place", params: { chi } },
        ],
      },
    ];
  }
  return [
    {
      family: "substitution",
      level: "strong",
      reasons: [
        { key: "ic-english", params: { ic } },
        { key: "letters-moved", params: { chi } },
      ],
    },
  ];
}

function periodicFinding(stats: Statistics): Finding | null {
  const ic = stats.ic ?? 0;
  const best = stats.periods[0];
  if (
    !best ||
    best.ic < PERIODIC_IC ||
    best.ic - ic < PERIODIC_GAIN ||
    best.sigma < PERIODIC_SIGMA
  ) {
    return null;
  }
  // Multiples of the true key length score as well as it does: take the smallest close one.
  const length =
    stats.periods
      .filter((guess) => best.ic - guess.ic <= PERIOD_TIE)
      .map((guess) => guess.length)
      .sort((a, b) => a - b)[0] ?? best.length;
  const reasons: Reason[] = [
    { key: "ic-between", params: { ic } },
    {
      key: "period-ic",
      params: {
        length,
        ic:
          stats.periods.find((guess) => guess.length === length)?.ic ?? best.ic,
      },
    },
  ];
  const kasiski = stats.kasiski.find((entry) => entry.factor === length);
  if (kasiski) {
    reasons.push({
      key: "kasiski-agrees",
      params: { factor: kasiski.factor, count: kasiski.count },
    });
  }
  return { family: "periodic", level: "strong", reasons };
}

/**
 * Playfair-type texts are written in pairs, and a pair of identical letters never occurs
 * in a Playfair digraph. In other ciphers one aligned pair in 26 or so is a double, so a
 * long text with none stands out.
 */
function digraphFinding(letters: string): Finding | null {
  const pairs = letters.length / 2;
  if (letters.length % 2 !== 0 || pairs < DIGRAPH_MIN_PAIRS) {
    return null;
  }
  let doubled = 0;
  for (let index = 0; index < letters.length; index += 2) {
    if (letters[index] === letters[index + 1]) {
      doubled += 1;
    }
  }
  if (doubled > 0) {
    return null;
  }
  const reasons: Reason[] = [
    { key: "even-length", params: { letters: letters.length } },
    { key: "no-doubled-pairs", params: { pairs } },
  ];
  if (!letters.includes("J")) {
    reasons.push({ key: "no-j" });
  }
  return {
    family: "digraph",
    level: pairs >= DIGRAPH_STRONG_PAIRS ? "strong" : "possible",
    reasons,
  };
}

function lowIcFindings(letters: string, stats: Statistics): Finding[] {
  const ic = stats.ic ?? 0;
  const digraph = digraphFinding(letters);
  const periodic = periodicFinding(stats);
  if (digraph?.level === "strong") {
    return periodic ? [digraph, { ...periodic, level: "possible" }] : [digraph];
  }
  if (periodic) {
    return digraph ? [periodic, digraph] : [periodic];
  }
  const flat = found("flat", ic <= FLAT_IC ? "strong" : "possible", {
    key: "ic-flat",
    params: { ic },
  });
  return digraph ? [digraph, flat] : [flat];
}

function letterFindings(text: string, stats: Statistics): Finding[] {
  const letters = lettersOnly(text);
  const first = simpleSubstitutionFinding(letters, stats);
  if (first) {
    return [first];
  }
  if ((stats.ic ?? 0) >= ENGLISH_LIKE_IC) {
    return englishLikeFindings(stats);
  }
  return lowIcFindings(letters, stats);
}

/* --------------------------------------------------------------- entry point */

/** Families decided by letter statistics, which a short text cannot support strongly. */
const STATISTICAL: ReadonlySet<FamilyId> = new Set([
  "english",
  "shift",
  "atbash",
  "affine",
  "substitution",
  "transposition",
  "periodic",
  "digraph",
  "flat",
]);

const LEVEL_ORDER: Record<Level, number> = { strong: 0, possible: 1, weak: 2 };

function foreignShare(text: string): number {
  const folded = foldDiacritics(text);
  let letters = 0;
  let foreign = 0;
  for (const char of folded) {
    if (ANY_LETTER.test(char)) {
      letters += 1;
      if (letterIndex(char) < 0) {
        foreign += 1;
      }
    }
  }
  return letters === 0 ? 0 : foreign / letters;
}

export function identify(text: string): Identification | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const stats = measure(trimmed);
  const findings: Finding[] = [...structuralFindings(trimmed, stats)];
  if (foreignShare(trimmed) >= 0.3) {
    findings.push({
      family: "other-script",
      level: "strong",
      reasons: [{ key: "other-script" }],
    });
  } else if (
    findings.length === 0 &&
    stats.letters > 0 &&
    stats.letters >= stats.characters * 0.6
  ) {
    findings.push(...letterFindings(trimmed, stats));
  }
  if (findings.length === 0) {
    findings.push({
      family: "symbols",
      level: "possible",
      reasons: [{ key: "mostly-symbols" }],
    });
  }
  const short = stats.letters < SHORT_TEXT;
  const settled = findings.map((finding) =>
    short && STATISTICAL.has(finding.family) && finding.level === "strong"
      ? { ...finding, level: "possible" as const }
      : finding
  );
  settled.sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]);
  return { stats, findings: settled, short };
}
